import { v4 as uuidv4 } from 'uuid';
import type { FileAttachment } from '../types/chat';
import { MAX_FILE_SIZE, ALLOWED_FILE_TYPES } from './constants';

export const FILE_TYPE_CATEGORIES = {
  office: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  document: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
  ],
  data: [
    'application/pdf',
  ],
};

export function getAcceptString(): string {
  const extensions: string[] = [];
  ALLOWED_FILE_TYPES.forEach(type => {
    if (type === 'application/vnd.ms-excel') extensions.push('.xls');
    else if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') extensions.push('.xlsx');
    else if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') extensions.push('.docx');
    else if (type === 'application/msword') extensions.push('.doc');
    else if (type === 'application/pdf') extensions.push('.pdf');
    // Добавить маппинг для других типов по необходимости
  });
  return extensions.join(',');
}

export function getFileCategory(fileType: string): string {
  for (const [category, types] of Object.entries(FILE_TYPE_CATEGORIES)) {
    if (types.includes(fileType)) {
      return category;
    }
  }
  return 'other';
}

export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes === 0) return 'Размер неизвестен';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function validateFile(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `Файл слишком большой. Максимальный размер: ${formatFileSize(MAX_FILE_SIZE)}`
    };
  }

  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Тип файла не поддерживается. Разрешены только: ${getAcceptString()}`
    };
  }

  return { valid: true };
}

export async function fileToAttachment(file: File): Promise<FileAttachment> {
  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const data = await fileToBase64(file);

  return {
    id: uuidv4(),
    name: file.name,
    type: file.type,
    size: file.size,
    data,
    uploadedAt: new Date(),
  };
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}

export function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

export function downloadFile(attachment: FileAttachment): void {
  if (attachment.url) {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  if (attachment.data) {
    const blob = base64ToBlob(attachment.data, attachment.type);
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.name;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Отложенная очистка URL, чтобы браузер успел начать скачивание
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
    return;
  }

  console.error('No file data or URL available for download');
}

export function getFileIcon(fileType: string): string {
  const category = getFileCategory(fileType);
  
  switch (category) {
    case 'document':
      return '📄';
    case 'office':
      return '📊';
    case 'data':
      return '📋';
    case 'archive':
      return '📦';
    default:
      return '📎';
  }
}
